import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { Platform, ProjectPageSummary } from "@koc/shared";
import { apiProjects, apiVideos } from "../api.js";
import { Badge, Button, Card, PageHeader, Table, fmtNum, fmtTime } from "../components/ui.js";
import { ProjectEditDialog } from "./ProjectsPage.js";

const PLATFORM_TONE: Record<Platform, "blue" | "yellow" | "red"> = {
  tiktok: "blue",
  instagram: "yellow",
  youtube: "red",
};

export function ProjectDetailPage() {
  const { projectId } = useParams({ from: "/_layout/projects/$projectId" });
  const id = Number(projectId);
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);

  const project = useQuery({ queryKey: ["project", id], queryFn: () => apiProjects.get(id) });
  const videos = useQuery({
    queryKey: ["videos", { projectId: id }],
    queryFn: () => apiVideos.list({ projectId: id, limit: 50 }),
  });

  const scrapeNow = useMutation({
    mutationFn: () => apiProjects.scrapeNow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs"] }),
  });

  const remove = useMutation({
    mutationFn: () => apiProjects.remove(id),
    onSuccess: () => { window.location.href = "/projects"; },
  });

  if (!project.data) return <div className="text-stone-500">Loading…</div>;
  const c = project.data;

  return (
    <>
      <PageHeader
        title={c.account_name}
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowEdit(true)}>Edit</Button>
            <Button onClick={() => scrapeNow.mutate()} disabled={scrapeNow.isPending}>
              {scrapeNow.isPending ? "Enqueuing…" : "Scrape now"}
            </Button>
            <Button variant="danger" onClick={() => { if (confirm("Delete project + all its videos?")) remove.mutate(); }}>
              Delete project
            </Button>
          </>
        }
      />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="text-xs text-stone-500 mb-1">Status</div>
          <div>{c.enabled ? <Badge tone="green">enabled</Badge> : <Badge tone="red">disabled</Badge>}</div>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Keywords</div>
          <div className="flex flex-wrap gap-1">
            {c.monitor_keywords.length === 0
              ? <span className="text-stone-400">none</span>
              : c.monitor_keywords.map((k) => <Badge key={k}>{k}</Badge>)}
          </div>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Last update</div>
          <div className="text-sm">{fmtTime(c.updated_at)}</div>
        </Card>
      </div>
      <Card className="mb-6">
        <div className="text-xs text-stone-500 mb-2">Pages ({c.pages.length})</div>
        {c.pages.length === 0
          ? <span className="text-sm text-stone-400">No pages. Click Edit to add one.</span>
          : (
            <ul className="divide-y divide-stone-100">
              {c.pages.map((a) => <PageRow key={a.id} page={a} />)}
            </ul>
          )}
      </Card>
      <h2 className="text-lg font-semibold mb-2">Recent videos</h2>
      <Table
        rows={videos.data?.items ?? []}
        columns={[
          { header: "Platform", cell: (r) => <Badge tone={PLATFORM_TONE[r.platform]}>{r.platform}</Badge>, className: "w-24" },
          { header: "Page", cell: (r) => <span className="text-xs text-stone-500">{r.page_label ?? "—"}</span>, className: "w-32" },
          { header: "KOC", cell: (r) => r.koc_id != null
            ? <Link to="/kocs/$kocId" params={{ kocId: String(r.koc_id) }} className="text-xs hover:underline">{r.koc_name ?? "—"}</Link>
            : <span className="text-xs text-stone-400">—</span>,
            className: "w-32" },
          { header: "Title", cell: (r) => (
            <Link to="/videos/$videoId" params={{ videoId: String(r.id) }} className="hover:underline">
              {r.title || <span className="text-stone-400">(no title)</span>}
            </Link>
          )},
          { header: "Hit", cell: (r) => r.has_keyword_hit ? <Badge tone="green">hit</Badge> : null, className: "w-16" },
          { header: "Views", cell: (r) => fmtNum(r.latest_view_count), className: "text-right tabular-nums w-24" },
          { header: "Likes", cell: (r) => fmtNum(r.latest_like_count), className: "text-right tabular-nums w-24" },
          { header: "Published", cell: (r) => <span className="text-xs text-stone-500">{fmtTime(r.publish_time)}</span> },
        ]}
      />
      {showEdit && (
        <ProjectEditDialog
          onClose={() => setShowEdit(false)}
          initialId={id}
          initial={{
            account_name: c.account_name,
            notes: c.notes,
            monitor_keywords: c.monitor_keywords,
            enabled: c.enabled,
            pages: c.pages.map((a) => ({
              id: a.id,
              koc_id: a.koc_id,
              platform: a.platform,
              profile_url: a.profile_url,
              label: a.label,
              enabled: a.enabled,
            })),
          }}
        />
      )}
    </>
  );
}

function PageRow({ page }: { page: ProjectPageSummary }) {
  return (
    <li className="flex items-center gap-3 py-2">
      <Badge tone={PLATFORM_TONE[page.platform]}>{page.platform}</Badge>
      <span className="text-sm font-medium text-stone-800 w-32 truncate">{page.label || <span className="text-stone-400">(no label)</span>}</span>
      <a href={page.profile_url} target="_blank" rel="noreferrer" className="text-sm text-stone-700 hover:underline truncate flex-1">
        {page.profile_url}
      </a>
      {page.koc_id != null
        ? (
          <Link to="/kocs/$kocId" params={{ kocId: String(page.koc_id) }} className="text-xs text-stone-600 hover:underline">
            KOC: {page.koc_name ?? `#${page.koc_id}`}
          </Link>
        )
        : <span className="text-xs text-stone-400">no KOC</span>}
      {page.enabled
        ? <Badge tone="green">on</Badge>
        : <Badge tone="red">off</Badge>}
    </li>
  );
}
